import React, { useRef, useState, Suspense, forwardRef, useEffect } from 'react'
import { THREE } from 'expo-three'
import { useFrame } from '@react-three/fiber'
import { Html, useAspect } from '@react-three/drei'
import { useCircle, useApi } from '../../systems/Physics/hooks'
import { useEvents } from '../../systems/World/hooks'
import { CHARACTER_EVENTS } from '../../systems/World/Events'
import Ufo from '../UfoModels/UfoModel1'
import Ufo2 from '../UfoModels/UfoModel2'
import Ufo3 from '../UfoModels/UfoModel3'
import Ufo4 from '../UfoModels/UfoModel4'
import anime from '../../helpers/anime'
import emoteSwirl from '../../../assets/emotes/emote_swirl.png'
import { PALETTES } from '../../constants/gameOptions'

/* PINK AND BLUE
'#022f4d',
'#660f1e',
'#4a4848',
'#e4e4ef'
*/

function SmokeInstances({ originMeshRef, color, seed = 0.3 }) {
  const [sphereRefs] = useState(() => [])
  const range = center => Math.random() * ((center + 0.4) - (center - 0.3)) + (center - 0.3)
  const rangeMinMax = (min, max) => Math.random() * (max - min) + min
  // const range = center => center - 0.35
  const initialPositions = [
    [range(originMeshRef.current.position.x - 0.2), originMeshRef.current.position.y - (seed * 1), range(originMeshRef.current.position.z)],
    [range(originMeshRef.current.position.x - 0.2), originMeshRef.current.position.y - (seed * 2), range(originMeshRef.current.position.z)],
    [range(originMeshRef.current.position.x - 0.2), originMeshRef.current.position.y - (seed * 3), range(originMeshRef.current.position.z)],
    [range(originMeshRef.current.position.x - 0.2), originMeshRef.current.position.y - (seed * 4), range(originMeshRef.current.position.z)],
    [range(originMeshRef.current.position.x - 0.2), originMeshRef.current.position.y - (seed * 5), range(originMeshRef.current.position.z)],
    [range(originMeshRef.current.position.x - 0.2), originMeshRef.current.position.y - (seed * 6), range(originMeshRef.current.position.z)],
  ]
  const INCREMENT = 0.02
  // smaller spheres movement
  useFrame(() => {
    // animate each sphere in the array
    sphereRefs.forEach((el, idx) => {
      el.position.y += INCREMENT
      
      if (el.position.y > originMeshRef.current.position.y + INCREMENT && el.position.y < originMeshRef.current.position.y + INCREMENT * 2) {
        el.material.opacity = 1
        // updates position
        el.position.x = range(originMeshRef.current.position.x - 0.2)
        el.position.z = range(originMeshRef.current.position.z - 0.2)
      }

      el.material.opacity -= 0.02
      if (el.position.y > 1.5) {
        el.material.opacity = 0
        el.position.y = originMeshRef.current.position.y - 0.3 * idx
        el.position.x = originMeshRef.current.position.x
        el.position.z = originMeshRef.current.position.z
      }
    })
  })
  return (
    <>
      {initialPositions.map((pos, i) => (
        <mesh
          position={[pos[0], pos[1], pos[2]]}
          key={i}
          ref={(ref) => (sphereRefs[i] = ref)}
        >
          {/* <boxBufferGeometry args={[0.2, 0.2, 0.2]}/> */}
          <sphereBufferGeometry args={[rangeMinMax(0.05, 0.2)]} />
          <meshBasicMaterial color={color} opacity={0} transparent />
        </mesh>
      ))}
    </>
  )
}
const Model = forwardRef((props, ref) => {
  const [isFreezed, setFreezed] = useState()
  const [emote, setEmote] = useState()
  const [powerMaterial] = useState(new THREE.MeshNormalMaterial({ transparent: true, opacity: 0, wireframe: false, side: THREE.BackSide }))
  const emoteTimerId = useRef()
  const powerRef = useRef()
  const ufoRef = useRef()
  const orignalColors = useRef({})
  const api = useApi()
  let baseRef = ref
  useEvents(CHARACTER_EVENTS.GETS_FREEZER, uuid => {
    if (uuid === baseRef.current.uuid && !isFreezed) {
      setFreezed(true)
    }
  })
  useEvents(CHARACTER_EVENTS.UNFREEZE, (uuid) => {
    if (uuid === baseRef.current.uuid && isFreezed) {
      api.unfreeze(baseRef.current.uuid)
      setFreezed(false)
    }
  })
  useEvents(CHARACTER_EVENTS.PICK_CRYSTAL_GROW, uuid => {
    if (uuid === baseRef.current.uuid) {
      const targetObj = { scale: 1 }
      anime({
        easing: 'linear',
        duration: 250,
        scale: 1.5,
        targets: targetObj,
        update: function () {
          baseRef.current.scale.set(targetObj.scale, targetObj.scale, targetObj.scale)
        }
      })
    }
  })
  const growOverchargeSphere = uuid => {
    if (uuid === baseRef.current.uuid) {
      powerRef.current.visible = true
      anime({
        easing: 'linear',
        duration: 250,
        opacity: 0.5,
        targets: powerMaterial,
      })
    }
  }
  const shrinkOverchargeSphere = uuid => {
    if (uuid === baseRef.current.uuid) {
      anime({
        easing: 'linear',
        duration: 250,
        opacity: 0,
        targets: powerMaterial,
        complete: function () {
          powerRef.current.visible = false
        }
      })
    }
  }
  useEvents(CHARACTER_EVENTS.PICK_CRYSTAL_COIL, growOverchargeSphere)
  useEvents(CHARACTER_EVENTS.PICK_CRYSTAL_FREEZER, growOverchargeSphere)
  useEvents(CHARACTER_EVENTS.DISCHARGE, shrinkOverchargeSphere)
  
  if (!props.userData.detached) {
    [baseRef] = useCircle({}, ref)
  } else {
  }
  useEvents(CHARACTER_EVENTS.EXTERNAL_SPIN_CHANGE, uuid => {
    if (uuid === baseRef.current.uuid) {
      setEmote(emoteSwirl)
    }
  })

  useEffect(() => {
    if (emote) {
      clearTimeout(emoteTimerId.current)
      emoteTimerId.current = setTimeout(() => {
        setEmote()
      }, 1000)
    }
  }, [emote])

  // const v1 = new THREE.Vector3(-0.25, 0, 0)
  // const v2 = new THREE.Vector3(0, -0.25, 0)
  // const v3 = new THREE.Vector3(0, 0.25, 0)
  
  const shape = new THREE.Shape();
  const x = 0;
  const y = 0;
  shape.moveTo(x - 0.2, y - 0.2);
  shape.lineTo(x + 0.2, y - 0.2);
  shape.lineTo(x, y + 0.2);
  const indicatorGeometry = new THREE.ShapeGeometry(shape);

  // const radius =  0.25;  
  // const segments =  1;  
  // const indicatorGeometry = new THREE.CircleGeometry(radius, segments);

  // indicatorGeometry.vertices.push(v1)
  // indicatorGeometry.vertices.push(v2)
  // indicatorGeometry.vertices.push(v3)

  // indicatorGeometry.faces.push(new THREE.Face3(0, 1, 2))
  // indicatorGeometry.computeFaceNormals()

  
  useEffect(() => {
    if (isFreezed === true) {
      const offColorMaterial = new THREE.MeshBasicMaterial({ color: '#666666' })
      const materials = ufoRef.current
      // group
      .children[0]
      // mesh
      .children[0]
      .material
      materials.forEach((material, i) => {
        orignalColors.current[i] = material.color
        material.color = new THREE.Color(i % 2 === 0 ? 0x666666 : 0x999999)
      })
      setTimeout(() => {
        const targetObj = { x: -0.15 }
        let times = 0
        const animation = anime({
          easing: 'linear',
          duration: 100,
          x: 0.15,
          targets: targetObj,
          loop: true,
          direction: 'alternate',
          update: function () {
            times++
            ufoRef.current.position.x = targetObj.x
            if (times >= 50) {
              ufoRef.current.position.x = 0
              animation.pause()
            }
          },
        })
      }, 4000);
    }

    if (isFreezed === false) {
      const materials = ufoRef.current
      // group
      .children[0]
      // mesh
      .children[0]
      .material
      materials.forEach((material, i) => {
        material.color.r = orignalColors.current[i].r
        material.color.g = orignalColors.current[i].g
        material.color.b = orignalColors.current[i].b
      })
    }
  }, [isFreezed])

  let UfoModel
  let indicatorOffset = [0, 0, 0]
  let indicatorColor
  switch (props.model) {
    case 1:
    default:
      UfoModel = Ufo
      // indicatorOffset = [0.5, 0.25, 0]
      indicatorOffset = [0.25, 0.25, 0]
      indicatorColor = 'black'
      break
    case 2:
      UfoModel = Ufo2
      indicatorOffset = [0.35, 0.35, 0]
      indicatorColor = 'red'
      break
    case 3:
      UfoModel = Ufo3
      indicatorOffset = [0.25, 0.35, 0]
      indicatorColor = 'blue'
      break
    case 4:
      UfoModel = Ufo4
      indicatorOffset = [0.25, 0.35, 0]
      indicatorColor = 'white'
      break
  }

  const indicatorMaterial = new THREE.MeshBasicMaterial({ color: indicatorColor })
  return (
    <Suspense fallback={null}>
      <>
        {baseRef.current && !isFreezed && (
          <SmokeInstances
            originMeshRef={baseRef}
            color={PALETTES[props.model || 1][0]}
            seed={Math.random() * (0.3 - 0.01) + 0.01} />
        )}
        <group
          ref={baseRef}
          {...props}
          dispose={null}
        >
          <Html>
            {
              emote && (
                <div style={{ backgroundColor: 'transparent', marginTop: -38*1.5, marginLeft: -32/2}}>
                  <img src={emote} />
                </div>
              )
            }
          </Html>
          <mesh
            ref={powerRef}
            visible={false}
            material={powerMaterial}
          >
            <sphereBufferGeometry args={[0.7, 10, 10]} />
          </mesh>
          {/* <mesh material={materials.snowRoad} geometry={nodes['Mesh enemy_ufoPurple_0'].geometry} />
          <mesh material={materials.purple} geometry={nodes['Mesh enemy_ufoPurple_1'].geometry} />
          <mesh castShadow material={colorMaterial} geometry={nodes['Mesh enemy_ufoPurple_2'].geometry} />
          <mesh material={materials.glass} geometry={nodes['Mesh enemy_ufoPurple_3'].geometry} /> */}
          <UfoModel
            ref={ufoRef}
            colorPalette={PALETTES[props.model || 1]}
            // position={[0, 0, 0]}
          />
          <mesh
            visible={!isFreezed}
            material={indicatorMaterial}
            rotation={[-THREE.Math.degToRad(90), -THREE.Math.degToRad(0), -THREE.Math.degToRad(90)]}
            position={indicatorOffset}
            geometry={indicatorGeometry}
          />
        </group>
      </>
    </Suspense>
  )
})

export default Model
